// Wave 7 — PWA registration helper.
// Registers the service worker on first load and handles "Add to Home
// Screen" install prompts via a custom event the InstallBanner consumes.

import { withCsrfHeader } from "@/lib/queryClient";

let deferredPrompt: any = null;

export function initPwa() {
  if (typeof window === "undefined") return;
  // Register SW
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js").catch((err) => {
        // Silent: SW failure shouldn't break the app.
        console.warn("[PWA] sw.js registration failed", err?.message);
      });
    });
  }
  // Install prompt: defer the event so we can re-trigger it from a UI button.
  window.addEventListener("beforeinstallprompt", (e: any) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new CustomEvent("entertaincrm:pwa:available"));
  });
}

export async function triggerInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferredPrompt) return "unavailable";
  deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return choice?.outcome === "accepted" ? "accepted" : "dismissed";
}

export function isPwaInstallAvailable(): boolean {
  return !!deferredPrompt;
}

// ── Push subscription helpers ────────────────────────────────────────
export async function subscribeToPush(): Promise<{ ok: boolean; reason?: string }> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { ok: false, reason: "Push not supported in this browser." };
  }
  const reg = await navigator.serviceWorker.ready;
  const keyRes = await fetch("/api/push/public-key").then((r) => r.json());
  const key = keyRes?.key;
  if (!key) return { ok: false, reason: "Server is missing VAPID keys. Ask your admin." };
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
    } catch (err: any) {
      return { ok: false, reason: err?.message || "Subscription failed" };
    }
  }
  const json: any = sub.toJSON();
  const body = {
    endpoint: json.endpoint,
    keys: json.keys,
    userAgent: navigator.userAgent,
  };
  const res = await fetch("/api/push/subscribe", {
    method: "POST",
    headers: await withCsrfHeader({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) return { ok: false, reason: "Server rejected subscription." };
  return { ok: true };
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}
